<script setup lang="ts">
	import { computed, reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import femaleAvatar from '@/assets/imgs/femaie_avatar.png'
	import maleAvatar from '@/assets/imgs/male_avantar.png'

	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import useDepartmentCostCenters from '@/composables/useDepartment'
	import useCompanyCostCenter from '@/modules/companies/composables/useCompanyCostCenter'
	import useCompanyDepartment from '@/modules/companies/composables/useCompanyDepartment'
	import { useCompanyGroups } from '@/modules/groups/composables/useCompanyGroups'
	import usePendingRequest from '@/modules/pendingRequests/composables/usePendingRequest'
	import { NewDataUser, PendingRequest } from '@/modules/pendingRequests/interfaces/pendingRequest'
	import useRoles from '@/modules/roles/composables/useRoles'
	import useUser from '@/modules/users/composables/useUser'
	import useUserRole from '@/modules/users/composables/useUserRole'
	import useUsersSearch from '@/modules/users/composables/useUsersSearch'
	import { User } from '@/modules/users/interfaces/user'
	import StyledInput from '@/components/StyledInput.vue'
	import ModalComponent from '@/components/ModalComponent.vue'
	import ShadowBox from '@/components/ShadowBox.vue'

	interface Props {
		approval: PendingRequest<NewDataUser>
		companyId: number | string
	}

	const props = defineProps<Props>()
	const emit = defineEmits<{
		done: []
		close: []
	}>()

	const toast = useToast()

	const supervisorFilter = ref<{ company?: number }>({ company: Number(props.companyId) })

	const { approveMutationAsync } = usePendingRequest<NewDataUser>({
		params: { id: props.approval.id },
		immediate: false,
	})

	const { updateUserAsync } = useUser({ id: String(props.approval.id), immediate: false })
	const { updateRoleAsync } = useUserRole({ immediate: false, params: { id: String(props.approval.id) } })

	const {
		data: roles,
		refetch: refetchRoles,
		setCompanyId,
		isLoading: loadingRoles,
	} = useRoles({ immediate: false })

	const {
		data: groups,
		setCompany,
		refetch: refetchGroups,
		isLoading: loadingGroups,
	} = useCompanyGroups({ immediate: false })

	const {
		departments,
		refetch: refetchDepartment,
		setCompanyId: setCompanyDep,
		isLoading: loadingDep,
	} = useCompanyDepartment({ immediate: false, params: {} })

	const form = reactive<{
		departmentId: number | undefined
		roleId: number | undefined
		groupId: number | undefined
		supervisorId: User | undefined
		costCenterId: number | undefined
	}>({
		departmentId: undefined,
		roleId: undefined,
		groupId: undefined,
		supervisorId: undefined,
		costCenterId: undefined,
	})

	const {
		costCenters,
		refetch: refetchCostCenter,
		isLoading: loadingCost,
		departameniId,
	} = useDepartmentCostCenters({
		immediate: false,
		params: { id: form.departmentId! },
	})

	const { users, setQuery: setUserQuery, isFetching: fetchingUsers, refetch: refetchUsers } =
		useUsersSearch({
			automaticSearch: true,
			params: { filters: supervisorFilter },
			immediate: false,
		})

	const { createCompanyCostCenterAsync, createCostCenterLoading } = useCompanyCostCenter()
	const costCenterModal = ref(false)
	const formCostCenter = reactive({ departmentId: '', name: '', key: '' })

	const approving = ref(false)

	const avatarSrc = computed(() => {
		const gender = props.approval.newData.gender
		if (gender === 'femenino') return femaleAvatar
		return maleAvatar
	})

	const stateLabel = computed(() => {
		if (props.approval.state === 'pending') return { text: 'Solicitud pendiente', cls: 'bg-amber-500' }
		if (props.approval.state === 'approved') return { text: 'Solicitud aprobada', cls: 'bg-green-500' }
		return { text: 'Solicitud rechazada', cls: 'bg-red-500' }
	})

	// Load company data on mount
	const loadCompanyData = () => {
		const id = Number(props.companyId)
		setCompanyId(id)
		refetchRoles()
		setCompany(String(props.companyId))
		refetchGroups()
		setCompanyDep(id)
		refetchDepartment()
		supervisorFilter.value.company = id
		refetchUsers()
	}

	loadCompanyData()

	watch(
		() => form.departmentId,
		(val) => {
			if (val) {
				departameniId.value = val
				refetchCostCenter()
			}
		},
		{ immediate: true }
	)

	const canApprove = () =>
		!!(form.departmentId && form.costCenterId && form.roleId && form.supervisorId)

	const handleApprove = async () => {
		if (!canApprove()) {
			toast.error('Completa todos los campos antes de aprobar')
			return
		}
		approving.value = true
		try {
			const data = await approveMutationAsync({
				params: { id: props.approval.id },
				config: {
					raiseToast: true,
					onSuccessToast: 'Solicitud aprobada con éxito',
					onErrorToast: 'No se pudo aprobar la solicitud',
				},
			})
			// UserResource + additional(message) viene envuelto en `data` (additional
			// fuerza el wrapper); el usuario creado está en data.data, no en la raíz.
			// Sin esto, id => undefined y pega a /api/users/undefined (500).
			const approvedUser = (data as any)?.data ?? data
			const userId = approvedUser?.id
			if (userId) {
				await updateUserAsync({
					departmentId: form.departmentId,
					costCenterId: form.costCenterId!,
					companyGroupId: form.groupId,
					supervisorId: form.supervisorId?.id,
					id: userId,
				})
				await updateRoleAsync({
					params: { id: userId, roles: [form.roleId!] },
					config: { raiseToast: false },
				})
				emit('done')
			}
		} catch (error: any) {
			// La aprobación en sí ya pudo haber tenido éxito (toast propio); esto
			// cubre el update de depto/centro/supervisor/rol que corre después —
			// useUser.ts ya no traga su error con un toast genérico, así que sin
			// esto un fallo aquí quedaba completamente silencioso.
			toast.error(
				error?.response?.data?.message ||
					'No se pudo completar la asignación del usuario aprobado'
			)
		} finally {
			approving.value = false
		}
	}

	const handleCreateCostCenter = async () => {
		if (!formCostCenter.departmentId || !formCostCenter.name || !formCostCenter.key) {
			toast.error('Completa todos los campos del centro de costos')
			return
		}
		try {
			await createCompanyCostCenterAsync({
				params: {
					...formCostCenter,
					departmentId: Number(formCostCenter.departmentId),
					companyId: Number(props.companyId),
				},
				config: {
					raiseToast: true,
					onSuccessToast: 'Centro de costos creado',
					onErrorToast: 'Error al crear centro de costos',
				},
			})
			costCenterModal.value = false
			await refetchCostCenter()
			formCostCenter.departmentId = ''
			formCostCenter.name = ''
			formCostCenter.key = ''
		} catch (error: any) {
			toast.error(error?.response?.data?.message)
		}
	}
</script>

<template>
	<div class="space-y-4">
		<!-- Basic info -->
		<div class="border border-gray-100 rounded-xl overflow-hidden">
			<div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
				<h3 class="text-sm font-semibold text-gray-900">Información básica</h3>
				<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-white" :class="stateLabel.cls">
					{{ stateLabel.text }}
				</span>
			</div>

			<div class="flex flex-col sm:flex-row items-center gap-4 p-4">
				<!-- Avatar -->
				<div class="flex-shrink-0 w-24 border-r border-gray-100 pr-4">
					<img :src="avatarSrc" alt="Avatar" class="w-20 h-20 object-cover rounded-full mx-auto" />
				</div>

				<!-- Fields -->
				<div class="flex gap-6 w-full">
					<div class="space-y-2">
						<div>
							<p class="text-xs text-gray-400">Nombre</p>
							<p class="text-sm font-semibold text-gray-800">{{ approval.newData.name }}</p>
						</div>
						<div>
							<p class="text-xs text-gray-400">Segundo nombre</p>
							<p class="text-sm font-medium text-gray-600">{{ approval.newData.secondname ?? 'No tiene' }}</p>
						</div>
					</div>
					<div class="space-y-2">
						<div>
							<p class="text-xs text-gray-400">Apellido</p>
							<p class="text-sm font-semibold text-gray-800">{{ approval.newData.lastname }}</p>
						</div>
						<div>
							<p class="text-xs text-gray-400">Segundo apellido</p>
							<p class="text-sm font-medium text-gray-600">{{ approval.newData.secondlastname ?? 'No tiene' }}</p>
						</div>
						<div>
							<p class="text-xs text-gray-400">Correo</p>
							<p class="text-sm font-medium text-gray-600">{{ approval.newData.email || '—' }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Assignment form -->
		<div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
			<StyledSelect
				v-model="form.departmentId"
				label="Departamento"
				is-required
				:options="departments?.map(d => ({ label: d.name, value: d.id })) || []"
				:loading-options="loadingDep"
				:disabled="loadingDep"
			/>

			<div
				v-if="form.departmentId"
				class="flex gap-2"
			>
				<StyledSelect
					v-model="form.costCenterId"
					label="Centro de costos"
					is-required
					:options="costCenters?.map(c => ({ label: c.name, value: c.id })) || []"
					:loading-options="loadingCost"
					:disabled="loadingCost"
					class="flex-1"
				/>
				<StyledButton
					type="button"
					class="bg-green-400 text-white hover:bg-green-500 mt-7 flex-shrink-0"
					v-tooltip="'Crear centro de costos'"
					@click="costCenterModal = true"
				>
					<span class="material-symbols-outlined">add</span>
				</StyledButton>
			</div>

			<StyledSelect
				v-model="form.roleId"
				label="Rol"
				is-required
				:options="roles?.map(r => ({ label: r.name, value: r.id })) || []"
				:loading-options="loadingRoles"
				:disabled="loadingRoles"
			/>

			<StyledSelect
				v-model="form.groupId"
				label="Grupo de viajes"
				is-required
				:options="groups?.map(g => ({ label: g.name, value: g.id })) || []"
				:loading-options="loadingGroups"
				:disabled="loadingGroups"
			/>

			<StyledCombobox
				class="sm:col-span-2"
				v-model="form.supervisorId"
				label="Supervisor"
				placeholder="Buscar supervisor..."
				:options="users"
				:loading-options="fetchingUsers"
				:display-value="(o) => `${o.name} ${o.lastname} (${o.email})`"
				:on-input-change="(v) => setUserQuery(v)"
				is-required
			>
				<template #default="{ option }">
					<span>{{ option.name }} {{ option.lastname }} ({{ option.email }})</span>
				</template>
			</StyledCombobox>
		</div>

		<!-- Actions -->
		<div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
			<StyledButton
				type="button"
				label="Cerrar"
				class="bg-gray-100 text-gray-700 hover:bg-gray-200"
				@click="emit('close')"
			/>
			<StyledButton
				type="button"
				label="Aprobar solicitud"
				class="text-white"
				:class="canApprove() ? 'bg-green-500 hover:bg-green-600' : 'bg-green-200 cursor-not-allowed'"
				:disabled="!canApprove() || approving"
				:loading="approving"
				@click="handleApprove"
			/>
		</div>
	</div>

	<!-- Cost center modal -->
	<ModalComponent
		:model-value="costCenterModal"
		@update:model-value="costCenterModal = false"
	>
		<template #modalBody>
			<ShadowBox>
				<h3 class="text-lg font-bold mb-4">Crear centro de costos</h3>
				<form
					class="flex flex-col gap-3"
					@submit.prevent="handleCreateCostCenter"
				>
					<StyledSelect
						label="Departamento"
						v-model="formCostCenter.departmentId"
						:options="departments?.map(d => ({ label: d.name, value: d.id })) || []"
					/>
					<StyledInput
						label="Llave"
						placeholder="Ej. 816"
						v-model="formCostCenter.key"
					/>
					<StyledInput
						label="Nombre"
						placeholder="Ej. Administración"
						v-model="formCostCenter.name"
					/>
					<div class="flex justify-between mt-2">
						<StyledButton
							type="button"
							label="Volver"
							class="bg-blue-700 text-white"
							@click="costCenterModal = false"
						/>
						<StyledButton
							type="submit"
							label="Crear"
							class="bg-[#253C70] text-white"
							:loading="createCostCenterLoading"
						/>
					</div>
				</form>
			</ShadowBox>
		</template>
	</ModalComponent>
</template>
